import { findIndex } from 'lodash'

import { ProductDimensions, ProductVariant } from 'hooks/brand/use-product'
import { ProductModifyPayload } from 'hooks/brand/use-update-product'

import { isValidUUID } from './functions'

type ProductVariantRequest = {
  id?: string
  key?: string
  master: boolean
  variantName?: string
  wholesalePrice?: number
  retailPrice?: number
  discountPrice?: number
  sku?: string
  width?: {
    value?: number
    unit: string
  }
  length?: {
    value?: number
    unit: string
  }
  height?: {
    value?: number
    unit: string
  }
  weight?: {
    value?: number
    unit: string
  }
  caseQuantity?: number
  minOrderQuantity?: number
  maxOrderQuantity?: number
  availability?: {
    quantityOnStock?: number
  }
} & ProductDimensions

const formatVariant = (
  variant: ProductVariant,
  caseQuantity?: number,
  minOrderQuantity?: number,
  maxOrderQuantity?: number,
  numberOfVariants?: number
) => {
  const {
    width,
    height,
    length,
    weight,
    productDimensionHeight,
    productDimensionLength,
    productDimensionWeight,
    productDimensionWidth,
    availability,
    ...restVariantData
  } = variant

  const formatted: ProductVariantRequest = {
    ...restVariantData,
    master: variant.master || numberOfVariants === 1,
    caseQuantity: Number(caseQuantity) > 0 ? Number(caseQuantity) : undefined,
    minOrderQuantity: Number(minOrderQuantity) > 0 ? Number(minOrderQuantity) : undefined,
    maxOrderQuantity: Number(maxOrderQuantity) > 0 ? Number(maxOrderQuantity) : undefined,
    ...(Number(height.value) > 0 && { height: { ...height, value: Number(height.value) } }),
    ...(Number(width.value) > 0 && { width: { ...width, value: Number(width.value) } }),
    ...(Number(length.value) > 0 && { length: { ...length, value: Number(length.value) } }),
    ...(Number(weight.value) > 0 && { weight: { ...weight, value: Number(weight.value) } }),
    discountPrice: Number(variant.discountPrice) > 0 ? Number(variant.discountPrice) : undefined,
    retailPrice: Number(variant.retailPrice) > 0 ? Number(variant.retailPrice) : undefined,
    wholesalePrice: Number(variant.wholesalePrice) > 0 ? Number(variant.wholesalePrice) : undefined,
    productDimensionHeight: Number(productDimensionHeight) > 0 ? Number(productDimensionHeight) : undefined,
    productDimensionLength: Number(productDimensionLength) > 0 ? Number(productDimensionLength) : undefined,
    productDimensionWeight: Number(productDimensionWeight) > 0 ? Number(productDimensionWeight) : undefined,
    productDimensionWidth: Number(productDimensionWidth) > 0 ? Number(productDimensionWidth) : undefined,
    availability: {
      quantityOnStock: typeof availability?.isOutOfStock === 'boolean' ? +!availability.isOutOfStock : 1,
    },
  }

  return formatted
}

export const formatProduct = (data: ProductModifyPayload) => {
  const product = data
  const caseQuantity = data.productVariants[0].caseQuantity
  const minOrderQuantity = data.productVariants[0].minOrderQuantity
  const maxOrderQuantity = data.productVariants[0].maxOrderQuantity
  const productionLeadTime = Number(data.productionLeadTime) > 0 ? Number(data.productionLeadTime) : undefined

  const productVariants: ProductVariantRequest[] = []

  if (data.multiVariant) {
    // Case 1: Handle Multi variants
    for (const variants of product.productVariants) {
      productVariants.push(
        formatVariant(variants, caseQuantity, minOrderQuantity, maxOrderQuantity, product.productVariants.length)
      )
    }
  } else if (product.productVariants.length > 0) {
    // Case 2: Handle First variant only
    productVariants.push(formatVariant(product.productVariants[0], caseQuantity, minOrderQuantity, maxOrderQuantity, 1))
  }

  let images = data.images

  if (data.id) {
    images = data.images?.map((image) => {
      const { id = '', url } = image

      return isValidUUID(id) ? { id } : { url }
    })
  }

  return { ...product, productionLeadTime, productVariants, images }
}

export const formatExistingProduct = (data: ProductModifyPayload) => {
  // Delete product id and key
  delete data.key
  delete data.id

  // Rename product
  data.name = data.name ? `${data.name} Copy` : ''

  data.productVariants.forEach((variant) => {
    // Delete SKU id and key
    delete variant.key
    delete variant.id

    // Rename SKU
    variant.sku = variant.sku ? `${variant.sku} (Copy${Date.now()})` : ''

    // Add unit if absent
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (variant.width && !variant.width.unit) variant.width.unit = ''
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (variant.height && !variant.height.unit) variant.height.unit = ''
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (variant.length && !variant.length.unit) variant.length.unit = ''
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (variant.weight && !variant.weight.unit) variant.weight.unit = ''
  })

  return data
}

export const convertDimensionUnit = (unit: string): string => {
  if (unit === 'in') {
    return "''"
  }
  return unit
}

export const hasDiscount = (
  wholeSalePrice: number | string | undefined | null,
  discountedPrice: number | string | undefined | null
) => {
  return !!discountedPrice && !!wholeSalePrice && wholeSalePrice !== discountedPrice
}

export const productSortFunc = (keyA: string, keyB: string, productsOrder: string[]) => {
  const indexA = findIndex(productsOrder, (o) => o === keyA) + 1
  const indexB = findIndex(productsOrder, (o) => o === keyB) + 1
  if (indexA === indexB) return 0
  return indexA - indexB
}
